import { Component } from '@theme/component';
import { morphSection } from '@theme/section-renderer';

class ProductRecommendations extends Component {
  /**
   * The observer for the product recommendations
   * @type {IntersectionObserver}
   */
  #intersectionObserver = new IntersectionObserver(
    (entries, observer) => {
      if (!entries[0]?.isIntersecting) return;

      observer.disconnect();
      this.#loadRecommendations();
    },
    { rootMargin: '0px 0px 400px 0px' }
  );

  /**
   * Observing changes to the elements attributes
   * @type {MutationObserver}
   */
  #mutationObserver = new MutationObserver((mutations) => {
    if (this.#ignoreMutations) return;

    for (const mutation of mutations) {
      // Only attribute changes are interesting
      if (mutation.target !== this || mutation.type !== 'attributes') continue;

      // Ignore error attribute changes
      if (mutation.attributeName === 'data-error') continue;

      // Ignore addition of hidden class because it means there's an error with the display
      if (mutation.attributeName === 'class' && this.classList.contains('hidden')) continue;

      // Ignore loading class toggles used by cart recommend refresh UX
      if (mutation.attributeName === 'class') continue;

      // Ignore when the data-recommendations-performed attribute has been set to 'true'
      if (
        mutation.attributeName === 'data-recommendations-performed' &&
        this.dataset.recommendationsPerformed === 'true'
      )
        continue;

      // All other attribute changes trigger a reload
      this.#loadRecommendations();
      break;
    }
  });

  /**
   * The cached recommendations
   * @type {Record<string, string>}
   */
  #cachedRecommendations = {};

  /**
   * An abort controller for the active fetch (if there is one)
   * @type {AbortController | null}
   */
  #activeFetch = null;

  /**
   * Suppress MutationObserver-driven reloads during programmatic refresh
   * @type {boolean}
   */
  #ignoreMutations = false;

  connectedCallback() {
    super.connectedCallback();
    this.#intersectionObserver.observe(this);
    this.#mutationObserver.observe(this, { attributes: true });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#intersectionObserver.disconnect();
    this.#mutationObserver.disconnect();
  }

  /**
   * Force-refresh recommendations (e.g. after add-to-cart from a cart recommend card).
   * Busts the in-memory cache so Liquid cart filters (is_in_cart) are re-evaluated.
   *
   * @param {{ bustCache?: boolean, showSkeleton?: boolean }} [options]
   * @returns {Promise<void>}
   */
  async reloadRecommendations({ bustCache = true, showSkeleton = false } = {}) {
    this.#ignoreMutations = true;

    try {
      if (bustCache) {
        this.#cachedRecommendations = {};
      }

      this.#activeFetch?.abort();
      this.#activeFetch = null;

      this.classList.remove('hidden');
      delete this.dataset.error;

      if (showSkeleton) {
        this.#renderLoadingSkeleton();
      }

      this.dataset.recommendationsPerformed = 'false';
      await this.#fetchAndMorphRecommendations();
    } finally {
      this.#ignoreMutations = false;
    }
  }

  /**
   * Load the product recommendations
   */
  #loadRecommendations() {
    const { recommendationsPerformed } = this.dataset;

    // If the recommendations have already been loaded, accounts for the case where the Theme Editor
    // is loaded the section from the editor's visual preview context.
    if (recommendationsPerformed === 'true') {
      return;
    }

    this.#fetchAndMorphRecommendations().catch((e) => {
      this.#handleError(e);
    });
  }

  /**
   * Fetch recommendations HTML and morph into the section.
   * @returns {Promise<void>}
   */
  async #fetchAndMorphRecommendations() {
    const { productId, sectionId, intent } = this.dataset;

    if (!productId || !sectionId) {
      throw new Error('Product ID and a section ID are required');
    }

    try {
      const result = await this.#fetchCachedRecommendations(productId, sectionId, intent);

      if (!result.success) {
        // The Theme Editor will place a section element element in the DOM whose section_id is not available
        // to the Section Renderer API. In this case, we can safely ignore the error.
        if (!Shopify.designMode) {
          this.#handleError(new Error(`Server returned ${result.status}`));
        }
        return;
      }

      if (result.data?.trim().length) {
        this.dataset.recommendationsPerformed = 'true';
        morphSection(sectionId, result.data, { mode: 'hydration', injectStylesheet: true });
      } else {
        this.#handleError(new Error('No recommendations available'));
      }
    } catch (e) {
      if (e instanceof Error && e.name === 'AbortError') return;
      throw e;
    }
  }

  /**
   * Replaces content with the same skeleton markup used by the Liquid loading state.
   */
  #renderLoadingSkeleton() {
    const headingText = this.querySelector('.cart-recomendados__heading')?.textContent?.trim() || '';
    const loadingLabel =
      this.dataset.loadingLabel ||
      globalThis.Theme?.translations?.loading_product_recommendations ||
      'Loading';

    const section = document.createElement('section');
    section.className = 'cart-recomendados__section cart-recomendados__section--loading';
    section.setAttribute('aria-busy', 'true');

    const header = document.createElement('div');
    header.className = 'cart-recomendados__header';

    if (headingText) {
      const heading = document.createElement('h2');
      heading.className = 'cart-recomendados__heading';
      heading.textContent = headingText;
      header.appendChild(heading);
    }

    const skeleton = document.createElement('div');
    skeleton.className = 'cart-recomendados__skeleton';
    skeleton.setAttribute('aria-label', loadingLabel);

    for (let i = 0; i < 2; i++) {
      const item = document.createElement('div');
      item.className = 'cart-recomendados__skeleton-item';
      skeleton.appendChild(item);
    }

    section.append(header, skeleton);
    this.replaceChildren(section);
  }

  /**
   * Fetches the recommendations and cached the result for future use
   * @param {string} productId
   * @param {string | undefined} sectionId
   * @param {string | undefined} intent
   * @returns {Promise<{ success: true, data: string } | { success: false, status: number }>}
   */
  async #fetchCachedRecommendations(productId, sectionId, intent) {
    const url = `${this.dataset.url}&product_id=${productId}&section_id=${sectionId}&intent=${intent}`;

    const cachedResponse = this.#cachedRecommendations[url];
    if (cachedResponse) {
      return { success: true, data: cachedResponse };
    }

    this.#activeFetch?.abort();
    this.#activeFetch = new AbortController();

    try {
      const response = await fetch(url, { signal: this.#activeFetch.signal });
      if (!response.ok) {
        return { success: false, status: response.status };
      }

      const text = await response.text();
      this.#cachedRecommendations[url] = text;
      return { success: true, data: text };
    } finally {
      this.#activeFetch = null;
    }
  }

  /**
   * Handle errors in a consistent way
   * @param {Error} error
   */
  #handleError(error) {
    console.error('Product recommendations error:', error.message);
    this.classList.add('hidden');
    this.dataset.error = 'Error loading product recommendations';
  }
}

if (!customElements.get('product-recommendations')) {
  customElements.define('product-recommendations', ProductRecommendations);
}
